import React from "react";
import "@testing-library/jest-dom";
import { DndContext, DragStartEvent } from "@dnd-kit/core";
import { fireEvent, render, screen, within } from "@testing-library/react";
import NestedCanvasGridElement from "./NestedCanvasGridElement";
import { renderElementPreview } from "../../views/CanvasBuilder";
import { useCanvasSensors } from "../../dnd/canvasSensors";
import type { CanvasBuilderElement } from "../../views/canvasBuilderProjection";
import type { CanvasGridConfig } from "../../types";

jest.mock("../MyVaultAgent", () => ({
  FloatingChatWidget: () => null,
}));

const nestedText: CanvasBuilderElement = {
  tempId: "nested-text-1",
  id: "nested-text-1",
  type: "text",
  props: { label: "Nested Text", content: "Example", variant: "text" },
  layout: { order: 1 },
  gridColumn: 1,
  gridRow: 2,
  columnSpan: 4,
  rowSpan: 1,
  parentElementId: "card-1",
  displayOrder: 1,
};

const gridConfig: CanvasGridConfig = {
  columns: 12,
  rowHeight: 60,
  gap: 8,
  minRows: 10,
};

const card: CanvasBuilderElement = {
  tempId: "card-1",
  id: "card-1",
  type: "container",
  props: {
    label: "Parent card",
    containerType: "card",
    internalGrid: { columns: 12 },
  },
  layout: { order: 0 },
  gridColumn: 1,
  gridRow: 1,
  columnSpan: 6,
  rowSpan: 6,
};

function Harness({
  onDragStart,
}: {
  onDragStart: (event: DragStartEvent) => void;
}) {
  const sensors = useCanvasSensors({ x: 120, y: 68 });
  const [isSelected, setIsSelected] = React.useState(false);
  return (
    <DndContext sensors={sensors} onDragStart={onDragStart}>
      <div role="grid">
        <NestedCanvasGridElement
          element={nestedText}
          isSelected={isSelected}
          isHovered={false}
          mode="edit"
          config={gridConfig}
          onSelect={() => setIsSelected(true)}
          onHover={jest.fn()}
          onDelete={jest.fn()}
          onDuplicate={jest.fn()}
          onResize={jest.fn()}
        >
          Nested preview
        </NestedCanvasGridElement>
      </div>
    </DndContext>
  );
}

describe("NestedCanvasGridElement keyboard accessibility", () => {
  it("exposes, selects, and lets keyboard users pick up a nested child", () => {
    const onDragStart = jest.fn();
    render(<Harness onDragStart={onDragStart} />);

    const child = screen.getByRole("button", {
      name: "Canvas element: Nested Text. Press Enter to select.",
    });
    expect(child.getAttribute("aria-pressed")).toBe("false");
    fireEvent.keyDown(child, { key: "Enter", code: "Enter" });
    expect(child.getAttribute("aria-pressed")).toBe("true");
    expect(within(child).getByTestId("nested-text-1-resize-east")).toHaveStyle({
      right: "2px",
      zIndex: "201",
    });
    expect(within(child).getByTestId("nested-text-1-resize-south")).toHaveStyle(
      { bottom: "2px", zIndex: "201" },
    );
    expect(
      within(child).getByTestId("nested-text-1-resize-corner"),
    ).toHaveStyle({ bottom: "2px", right: "2px", zIndex: "202" });
    expect(within(child).getByTestId("nested-text-1-controls")).toHaveStyle({
      top: "4px",
      right: "4px",
    });

    const dragHandle = within(child).getByRole("button", {
      name: "Move Nested Text",
    });
    // The role description is not queryable through getByRole's options.
    // eslint-disable-next-line testing-library/no-node-access
    expect(dragHandle.getAttribute("aria-roledescription")).toBe("draggable");
    fireEvent.focus(dragHandle);
    fireEvent.keyDown(dragHandle, { key: " ", code: "Space" });

    expect(onDragStart).toHaveBeenCalledTimes(1);
    fireEvent.keyDown(dragHandle, { key: "Escape", code: "Escape" });
  });

  it("uses the draggable wrapper in the actual card preview", () => {
    const renderNestedElement = jest.fn((element: CanvasBuilderElement) => (
      <NestedCanvasGridElement
        key={element.tempId}
        element={element}
        isSelected={false}
        isHovered={false}
        mode="edit"
        config={gridConfig}
        onSelect={jest.fn()}
        onHover={jest.fn()}
        onDelete={jest.fn()}
        onDuplicate={jest.fn()}
        onResize={jest.fn()}
      >
        Nested preview
      </NestedCanvasGridElement>
    ));
    render(
      <DndContext>
        {renderElementPreview(
          card,
          [card, nestedText],
          null,
          null,
          jest.fn(),
          renderNestedElement,
        )}
      </DndContext>,
    );

    expect(renderNestedElement).toHaveBeenCalledWith(nestedText);
    expect(
      screen.getByRole("button", {
        name: "Canvas element: Nested Text. Press Enter to select.",
      }),
    ).toBeTruthy();
  });
});
